/**
 * @file 日志公参，基本确定不变的数据
 * @author <zhangshen@kuaishou.com>
 */
import type { Base } from '../proto/base';
import type { DeepPartial } from 'utility-types';
import { H5ExtraAttr, networkType, BaseOptions, GlobalAttr } from '@/config/types';
import { flattenAssign } from '@/util';

export type CommonPackageOptions = DeepPartial<Base.CommonPackage>;
export type ICommonPackage = typeof CommonPackage;

export class CommonPackage implements Base.CommonPackage {
    // 默认值为 undefined 减小上报时字段
    // 标识信息
    identity_package: Base.IdentityPackage = {
        device_id: undefined,
        global_id: undefined,
        user_id: undefined,
        union_id: undefined,
        open_id: undefined,
        iu_id: undefined
    };
    // 应用信息
    app_package: Base.AppPackage = {
        product: undefined,
        language: undefined,
        platform: undefined,
        container: 'H5',
        package_name: undefined,
        product_name: undefined,
        version_name: undefined,
        channel: undefined,
        version_code: undefined,
    };
    experiment?: any[] = undefined;
    service_name?: string = undefined;
    safety_id?: string = undefined;
    sub_biz?: string = undefined;
    // 设备信息
    device_package: Base.DevicePackage = {
        os_version: undefined,
        model: undefined,
        ua: undefined,
    };
    need_encrypt: boolean = false;
    network_package: Base.NetworkPackage = {
        type: networkType.unknown,
    };
    // H5 附加参数
    h5_extra_attr: H5ExtraAttr = {
        // SDK 版本信息
        sdk_name: '__SDK_NAME__',
        sdk_version: '__VERSION__',
        sdk_bundle: '__SDK_BUNDLE__',
    };
    global_attr: GlobalAttr = {
        // 染色参数
        entry_tag: [],
    };
    location_package?: Base.LocationPackage = {
        country: undefined, // 国家
        province: undefined, // 省份/自治区/州
        city: undefined, // 城市
        county: undefined, // 县/区域
        street: undefined, // 街道
        latitude: undefined, // 纬度
        longitude: undefined, // 经度
    };

    constructor(options: CommonPackageOptions | BaseOptions) {
        this.update(options);
        if (this.app_package.version_name) {
            const lastDotIndex = this.app_package.version_name.lastIndexOf('.');
            this.app_package.version_code = +this.app_package.version_name.slice(lastDotIndex + 1) || 0;
        }
        if (!this.app_package.version_name) {
            this.app_package.version_name = undefined;
        }
        if (!this.app_package.version_code) {
            this.app_package.version_code = undefined;
        }
    }

    getH5ExtraAttr(extraInfo?: {[key: string]: any}) {
        return Object.assign({}, this.h5_extra_attr, extraInfo);
    }
    /**
     * 更新 commonPackage 信息
     * @param options
     * @returns
     */
    update(options: CommonPackageOptions | BaseOptions) {
        if (typeof options !== 'object') return;
        flattenAssign(this, options);
        // network type 特殊处理
        const network = (options as BaseOptions).network_type;
        if (network && (networkType as any)[network]) {
            this.network_package.type = (networkType as any)[network];
        }
    }
    /**
     * 更新 global_attr 信息
     * @param options
     */
    updateGlobalAttr(options: {[key: string]: any}) {
        Object.assign(this.global_attr || {}, options);
    }
    /**
     * 将 commonPackage 转换为 json 输出
     * @returns
     */
    toJSON() {
        // 处理 v2 空字符串接口会报错问题
        if (!this.identity_package.user_id) {
            this.identity_package.user_id = undefined;
        }
        const json: Base.CommonPackage = {
            ...this,
            toJSON() {
                return {
                    ...json,
                    h5_extra_attr: JSON.stringify(json.h5_extra_attr),
                    global_attr: JSON.stringify(json.global_attr),
                };
            }
        };
        json.global_attr = {...this.global_attr};
        if (this.global_attr.entry_tag && this.global_attr.entry_tag.length) {
            json.global_attr.entry_tag = this.global_attr.entry_tag.slice();
        } else {
            // 节省上报字段
            delete json.global_attr.entry_tag;
        }
        if (!Object.keys(this.global_attr).length) {
            // 节省上报字段
            delete json.global_attr;
        }
        const location = this.location_package;
        if (location) {
            Object.keys(location).forEach((key) => {
                if (typeof location[key as keyof Base.LocationPackage] === 'undefined') {
                    delete location[key as keyof Base.LocationPackage];
                }
            });
        }
        return json;
    }
}
